import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/calculator/extract-price`;
const MAX_BYTES = 30 * 1024 * 1024;
const allowedMime = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"]);
const allowedName = /\.(jpe?g|png|webp|heic|heif)$/i;

function photoError(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (error.code === "ECONNABORTED") return "Het uitlezen duurt te lang. Probeer dezelfde foto opnieuw.";
  if (error.response) return "De foto kon niet worden verwerkt. Probeer opnieuw of kies een andere foto.";
  return error.message === "Network Error" ? "Geen verbinding. Je gekozen foto blijft beschikbaar zolang deze pagina open blijft." : error.message || "Foto toevoegen is mislukt. Probeer opnieuw.";
}

export function useLabelPhoto(onExtract, onBusy) {
  const [photo, setPhoto] = useState(null), [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""), [error, setError] = useState("");
  const request = useRef(null), resultRef = useRef(onExtract), busyRef = useRef(onBusy);
  resultRef.current = onExtract; busyRef.current = onBusy;
  useEffect(() => () => { request.current?.abort(); busyRef.current(false); }, []);
  useEffect(() => () => { if (photo?.url) URL.revokeObjectURL(photo.url); }, [photo]);
  const cancel = () => { request.current?.abort(); request.current = null; setBusy(false); busyRef.current(false); };
  const clear = () => { cancel(); setPhoto(null); setError(""); setStatus(""); };
  const run = async (file, materialized = false) => {
    cancel(); setError(""); setStatus("");
    if (!file?.size) { setError("Deze foto is leeg of niet leesbaar. Kies de foto opnieuw."); return; }
    if (!allowedMime.has(file.type?.toLowerCase()) && !allowedName.test(file.name || "")) { setError("Kies een JPG-, PNG-, WebP- of HEIC/HEIF-foto."); return; }
    if (file.size > MAX_BYTES) { setError("Deze foto is te groot. Kies een foto van maximaal 30 MB."); return; }
    const controller = new AbortController(); request.current = controller;
    setBusy(true); busyRef.current(true); setStatus("Foto voorbereiden…");
    try {
      let readable = file;
      if (!materialized) {
        let bytes;
        try { bytes = await file.arrayBuffer(); } catch { throw new Error("De foto kan niet van je apparaat worden gelezen. Kies dezelfde foto opnieuw."); }
        if (controller.signal.aborted) return;
        if (bytes.byteLength !== file.size) throw new Error("De foto is niet volledig ingelezen. Kies dezelfde foto opnieuw.");
        readable = new File([bytes], file.name || "etiket.jpg", { type: file.type || "application/octet-stream" });
        setPhoto({ file: readable, url: URL.createObjectURL(readable) });
      }
      setStatus("Foto versturen…");
      const body = new FormData(); body.append("image", readable, readable.name);
      const { data } = await axios.post(API, body, {
        signal: controller.signal, timeout: 90000,
        onUploadProgress: (event) => { if (!controller.signal.aborted) setStatus(event.total && event.loaded < event.total ? `Foto versturen… ${Math.round(event.loaded * 100 / event.total)}%` : "Etiket lezen…"); },
      });
      if (controller.signal.aborted) return;
      resultRef.current(data);
      if (!data.normale_prijs && !data.artikelnaam && !data.artikelcode) { setError("Geen leesbare artikelgegevens gevonden. Kies een scherpere foto of vul de lege velden handmatig in."); setStatus(""); return; }
      const missing = [!data.normale_prijs && "normale prijs", !data.artikelnaam && "artikelnaam", !data.artikelcode && "artikelcode"].filter(Boolean);
      setStatus(missing.length ? `Etiket gelezen. Niet herkend: ${missing.join(", ")}.` : "Etiket gelezen. Controleer de ingevulde gegevens.");
    } catch (error) {
      if (!controller.signal.aborted) { setError(photoError(error)); setStatus(""); }
    } finally {
      if (!controller.signal.aborted) { setBusy(false); busyRef.current(false); }
    }
  };
  return { photo, busy, status, error, choose: (file) => { if (file) run(file); }, retry: () => { if (photo) run(photo.file, true); }, clear };
}